import { inject, Injectable } from '@angular/core';
import { DbService } from '../db/db.service';
import { environment } from '../../../environments/environment';
import { Project } from '../../models/project';
import { Title } from '@angular/platform-browser';
import { isValidHttpUrl, isValidString } from '@pineapplelab/util';
import { BrowserStorage } from '../../models/browserStorage';
import { jwtDecode } from 'jwt-decode';

@Injectable({
	providedIn: 'root'
})
export class ConfigService {

	private _projectConfig: Project = null;

	private readonly db = inject(DbService);
	private readonly titleService = inject(Title);

	getConfig(): Project {
		return this._projectConfig;
	}

	setProjectTheme(data: string): void {
		const link = document.createElement('link');
		link.rel = 'stylesheet';
		link.href = data;
		document.head.appendChild(link);
		this.setProjectFavicon();
	}

	private setProjectFavicon(): void {
		if (!environment?.favicon || !isValidHttpUrl(environment?.favicon)) {
			return;
		}
		const favicon: HTMLLinkElement = document.querySelector('#favIcon');
		if (favicon) {
			favicon.href = environment.favicon;
		}
	}

	async loadConfig(): Promise<void> {
		const project = await this.db.getProyIdRef(environment.projectId).get();
		if (!project.exists) {
			return null;
		}
		this._projectConfig = new Project(project.data());
		this.setWebsiteTitle(this._projectConfig?.name);
	}

	private setWebsiteTitle(title: string): void {
		if (isValidString(title)) {
			this.titleService.setTitle(title);
		}
	}

	/**
	 * Function runs when app is started in development and checks that the tokens are valid for the current project/tenant
	 * if the user is not a user from the current tenant then it logs out. This does not run for production, only meant for devs.
	 */
	shouldUserLogOut(): boolean {
		const userToken = BrowserStorage.get('userToken');
		if (!userToken) {
			return false;
		}
		try {
			const currentUserData = jwtDecode(userToken) as any;
			const userProjectId = currentUserData?.projectId;
			return userProjectId && isValidString(userProjectId) && environment.projectId !== userProjectId;
		} catch(e) {
			return true;
		}
	}

	getTenantLanguages(): string[] {
		return this._projectConfig?.languages;
	}

	isFeatureFlagEnabled(featureFlag: keyof Project, userIds: string[]): boolean {
		const allowedUsers = this._projectConfig?.[featureFlag] as string[];
		if (!Array.isArray(allowedUsers) || allowedUsers.length === 0) {
			return true;
		}
		return allowedUsers.some(id => userIds.includes(id));
	}


}
